import React, { useMemo, useRef } from 'react';
import { TextField as MuiTextField } from '@mui/material';
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import { DesktopDatePicker, DesktopDateTimePicker, DesktopTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import type { Moment } from 'moment';
import moment from 'moment';

import SingleLineLabelWithTooltip, {
  SINGLE_LINE_LABEL_CLASS_NAME,
} from '~/components/core/Molecules/Fields/SingleLineLabelWithTooltip';
import type { TextFieldProps } from '~/components/core/Molecules/Fields/TextField';
import { maskableFormat } from '~/DateTimeUtils';
import { isLocalEnv, reportErrorInProductionOrThrow } from '~/Utils';
import cn from '~/Utils/cn';

export interface DatePickerFieldProps extends Omit<TextFieldProps<string | null>, 'onChange' | 'variant'> {
  onChange: (newValue: PickerDate) => void;
  isDateTimeSelection?: boolean;
  isOnlyTimeSelection?: boolean;
  format?: string;
  disableFuture?: boolean;
  disablePast?: boolean;
  containerClassName?: string;
  placeholder?: string;
  onError?: (error: React.ReactNode) => void;
  clearable?: boolean;
  nullIfEmpty?: boolean;
  open?: boolean;
  onClose?: () => void;
  // Fires once with the final value when a selection is accepted (not on dismiss) — use this,
  // not onChange, to commit a picked value (e.g. persist to the server).
  onAccept?: (value: PickerDate) => void;
  fullWidth?: boolean;
  minDate?: string;
  keyboardIcon?: React.ReactNode;
}

const DATE_TIME_VIEWS = ['year', 'month', 'day', 'hours', 'minutes'] as const;

const DATE_VIEWS = ['year', 'month', 'day'] as const;

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  isDateTimeSelection = false,
  isOnlyTimeSelection = false,
  format,
  value,
  placeholder,
  onChange,
  onBlur,
  error,
  onError,
  helperText,
  containerClassName,
  open,
  onClose,
  onAccept,
  disablePast,
  disableFuture,
  clearable,
  fullWidth,
  label,
  className,
  id,
  disabled,
  minDate,
  keyboardIcon,
  InputLabelProps,
  clampLabelToSingleLine,
  ...rest
}) => {
  if (Object.keys(rest).length > 0 && isLocalEnv()) {
    reportErrorInProductionOrThrow(
      `Additional unimplemented props was passed to DatePickerField component ${Object.keys(rest)}`,
      true
    );
  }

  const renderedLabel = clampLabelToSingleLine && label ? <SingleLineLabelWithTooltip label={label} /> : label;
  // `pe-40` reserves room for the picker's always-present end-adornment
  const mergedInputLabelProps = {
    ...InputLabelProps,
    className: cn('pe-40', { [SINGLE_LINE_LABEL_CLASS_NAME]: clampLabelToSingleLine }, InputLabelProps?.className),
  };

  // Consumers render onError's argument as helper text, so it must be a human-readable message —
  // but the picker reports machine reason codes, so map them to messages.
  const translatePickerError = (reason: string | null): string => {
    switch (reason) {
      case null:
        return '';
      case 'maxDate':
      case 'disableFuture':
      case 'maxTime':
        return 'Date should not be after maximal date';
      case 'minDate':
      case 'disablePast':
      case 'minTime':
        return 'Date should not be before minimal date';
      default:
        return isOnlyTimeSelection ? 'Invalid Time Format' : 'Invalid Date Format';
    }
  };
  const handleError = onError ? (reason: string | null) => onError(translatePickerError(reason)) : undefined;

  const renderPickerInput = (params: MuiTextFieldProps) => (
    <MuiTextField
      {...params}
      variant="standard"
      size="small"
      id={id}
      className={className}
      fullWidth={fullWidth}
      onBlur={onBlur}
      // The red border is driven by the caller's `error` only — deliberately overriding the `error`
      // (= params.error) the `{...params}` spread above carries. v5 derives params.error from the value
      // ungated by touch, so it reddens a mid-typed/incomplete or invalid-on-mount value before any
      // message shows; callers gate `error` and `helperText` together (the Formik wrappers do) and still
      // get the picker's validation via `onError`, keeping border and message in sync.
      error={error}
      helperText={helperText}
      label={renderedLabel}
      InputLabelProps={{ ...params.InputLabelProps, ...mergedInputLabelProps }}
      // Only override the picker's placeholder when a caller supplies one; otherwise keep v5's
      // format hint (e.g. MM/DD/YYYY, hh:mm A) that `params.inputProps.placeholder` carries.
      inputProps={{ ...params.inputProps, ...(placeholder ? { placeholder } : {}) }}
    />
  );

  // The slot component's identity must be stable across renders, or MUI remounts the icon each time.
  // Consumers pass inline JSX, so `keyboardIcon` is a fresh object every render — memoizing on it
  // would rebuild the component anyway. Keep the icon in a ref and key the memo on its presence, so
  // the component type is created once and always reads the latest icon.
  const keyboardIconRef = useRef(keyboardIcon);
  keyboardIconRef.current = keyboardIcon;
  const hasKeyboardIcon = keyboardIcon !== undefined;
  const pickerComponents = useMemo(
    () => (hasKeyboardIcon ? { OpenPickerIcon: () => <>{keyboardIconRef.current}</> } : undefined),
    [hasKeyboardIcon]
  );

  const commonPickerProps = {
    onChange,
    ...(onAccept ? { onAccept } : {}),
    onError: handleError,
    disabled,
    renderInput: renderPickerInput,
    ...(clearable ? { componentsProps: { actionBar: { actions: ['clear' as const] } } } : {}),
    ...(pickerComponents ? { components: pickerComponents } : {}),
    // Only forward open/onClose when the caller actually controls them — passing `open={undefined}`
    // makes the picker permanently closed (controlled with no value) and the icon click does nothing.
    ...(open !== undefined ? { open, onClose } : {}),
    OpenPickerButtonProps: {
      // Override default -12px margin, to make a little bit of space between the icon and the right edge of the input.
      className: '-mr-[6px]',
    },
  };

  // Locale-derived default formats, each padded to fixed-length tokens (see maskableFormat) so the
  // picker keeps its input mask. Only reached when the caller passes no explicit `format`.
  const localeDateFormat = moment.localeData().longDateFormat('L');
  const localeTimeFormat = moment.localeData().longDateFormat('LT');
  const maskableDateFormat = maskableFormat(localeDateFormat);
  const maskableTimeFormat = maskableFormat(localeTimeFormat);
  const maskableDateTimeFormat = maskableFormat(`${localeDateFormat} ${localeTimeFormat}`);

  return (
    <div className={containerClassName}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        {isDateTimeSelection ? (
          <DesktopDateTimePicker
            // Default to the locale's date + short-time format, like the standalone date and time
            // variants: v5 derives am/pm — and the acceptRegex with it — from the locale, so a
            // hardcoded 12h mask over a 24h-locale clock would reject typed input. Most consumers pass
            // an explicit format; this default only reaches those that don't.
            inputFormat={format ? format : maskableDateTimeFormat}
            value={value ? moment(value) : null}
            disablePast={disablePast}
            disableFuture={disableFuture}
            views={DATE_TIME_VIEWS}
            // Lift AM/PM into the clock; without a toolbar they otherwise sit at the clock's bottom
            // corners, where the `clearable` action bar's Clear button overlaps the PM button.
            ampmInClock
            {...commonPickerProps}
          />
        ) : isOnlyTimeSelection ? (
          <DesktopTimePicker
            // Default to the locale's short-time format (`hh:mm A` for 12h locales, `HH:mm` for 24h)
            // rather than a hardcoded 12h mask: v5 derives am/pm — and the mask and acceptRegex with
            // it — from the locale, so pinning '12h' would force a 12h mask over a 24h-locale clock
            // that rejects typed AM/PM. (An undefined inputFormat crashes the picker's mask helper,
            // so the default must be a real locale-derived format.)
            inputFormat={format ? format : maskableTimeFormat}
            value={value ? moment(value, 'HH:mm') : null}
            // See the datetime picker above: lift AM/PM into the clock so the Clear button does not
            // overlap the PM button.
            ampmInClock
            {...commonPickerProps}
          />
        ) : (
          <DesktopDatePicker
            inputFormat={format ? format : maskableDateFormat}
            value={value ? moment(value, 'YYYY-MM-DD') : null}
            disableFuture={disableFuture}
            views={DATE_VIEWS}
            {...(minDate ? { minDate: moment(minDate) } : {})}
            {...commonPickerProps}
          />
        )}
      </LocalizationProvider>
    </div>
  );
};

export default DatePickerField;
export type PickerDate = Moment | null;
